import React, { useRef, useState } from "react";
import { Button, DatePicker, Modal, Space, message } from "antd";
import { useReactToPrint } from "react-to-print";
import QRCode from "react-qr-code";

import "./styles.scss";

const LABEL_WIDTH_MM = 30;
const LABEL_HEIGHT_MM = 15;
const EXPORT_DPI = 300;

const mmToCanvasPx = (mm) => Math.round((mm / 25.4) * EXPORT_DPI);

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

const fitText = (ctx, text, maxWidth) => {
  const normalized = String(text || "")
    .trim()
    .toUpperCase();
  if (!normalized) {
    return "";
  }

  if (ctx.measureText(normalized).width <= maxWidth) {
    return normalized;
  }

  let output = normalized;
  while (
    output.length > 0 &&
    ctx.measureText(`${output}...`).width > maxWidth
  ) {
    output = output.slice(0, -1);
  }

  return output ? `${output}...` : normalized;
};

const pageStyle = `
  @page {
    size: 30mm 15mm;
    margin: 1mm;
  }
`;

const Index = ({ currentItem, printOnly = false }) => {
  const componentRef = useRef();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [mfgdate, setmfgdate] = useState(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle,
  });

  const trytoprint = () => {
    if (mfgdate) {
      handlePrint();
      setIsModalVisible(false);
    } else {
      message.error("Enter Manufacturing Date");
    }
  };

  const exportToPng = async () => {
    if (currentItem.shelf_life && currentItem.expires && !mfgdate) {
      message.error("Enter Manufacturing Date");
      return;
    }

    const qrSvg = componentRef.current?.querySelector("svg");
    if (!qrSvg) {
      message.error("Unable to generate barcode image.");
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = mmToCanvasPx(LABEL_WIDTH_MM);
    canvas.height = mmToCanvasPx(LABEL_HEIGHT_MM);

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      message.error("Canvas export is not supported in this browser.");
      return;
    }

    try {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const qrMarkup = new XMLSerializer().serializeToString(qrSvg);
      const qrBlob = new Blob([qrMarkup], {
        type: "image/svg+xml;charset=utf-8",
      });
      const qrUrl = URL.createObjectURL(qrBlob);

      try {
        const qrImage = await loadImage(qrUrl);
        const rotatedWidth = canvas.height;
        const paddingX = mmToCanvasPx(0.5);
        const paddingY = mmToCanvasPx(0.7);
        const qrSize = mmToCanvasPx(8.5);
        const gap = mmToCanvasPx(0.9);
        const nameFontSize = mmToCanvasPx(1.25);
        const priceFontSize = mmToCanvasPx(1.25);
        const nameLineHeight = mmToCanvasPx(3.3);
        const availableTextWidth = rotatedWidth - paddingX * 2;
        const startY = paddingY;

        ctx.save();
        ctx.translate(0, canvas.height);
        ctx.rotate(-Math.PI / 2);

        ctx.drawImage(
          qrImage,
          (rotatedWidth - qrSize) / 2,
          startY,
          qrSize,
          qrSize,
        );

        ctx.fillStyle = "#111111";
        ctx.textAlign = "center";
        ctx.textBaseline = "top";

        ctx.font = `700 ${nameFontSize}px Arial`;
        ctx.fillText(
          fitText(ctx, currentItem.name, availableTextWidth),
          rotatedWidth / 2,
          startY + qrSize + gap,
        );

        ctx.font = `500 ${priceFontSize}px Arial`;
        ctx.fillText(
          `PHP ${Number(currentItem.sell_price || 0).toFixed(2)}`,
          rotatedWidth / 2,
          startY + qrSize + gap + nameLineHeight,
        );

        ctx.restore();

        const fileNameBase = String(
          currentItem.name || currentItem.id || "barcode",
        )
          .replace(/[^\w-]+/g, "_")
          .replace(/^_+|_+$/g, "");

        const dataUrl = canvas.toDataURL("image/png");
        const anchor = document.createElement("a");
        anchor.href = dataUrl;
        anchor.download = `${fileNameBase || "barcode"}_30x15mm.png`;
        anchor.click();

        if (isModalVisible) {
          setIsModalVisible(false);
        }

        message.success("Barcode PNG exported.");
      } finally {
        URL.revokeObjectURL(qrUrl);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to export barcode PNG.");
    }
  };

  return (
    <>
      <div className={"printItemhide"}>
        <ComponentToPrint
          ref={componentRef}
          currentItem={currentItem}
          mfgdate={mfgdate}
        />
      </div>
      {currentItem.shelf_life && currentItem.expires ? (
        <Space wrap>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setIsModalVisible(true);
            }}
            style={{ margin: "1ch" }}
            type={"primary"}
          >
            {printOnly ? "Print QR" : "Print Barcode"}
          </Button>
          {!printOnly && <Button
            onClick={(e) => {
              e.stopPropagation();
              setIsModalVisible(true);
            }}
            style={{ margin: "1ch" }}
          >
            Export PNG
          </Button>}
        </Space>
      ) : (
        <Space wrap>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handlePrint();
            }}
            style={{ margin: "1ch" }}
            type={"primary"}
          >
            {printOnly ? "Print QR" : "Print Barcode"}
          </Button>
          {!printOnly && <Button
            onClick={(e) => {
              e.stopPropagation();
              exportToPng();
            }}
            style={{ margin: "1ch" }}
          >
            Export PNG
          </Button>}
        </Space>
      )}
      <Modal
        title="Manufacturing Date"
        width={"30ch"}
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsModalVisible(false);
            }}
          >
            Cancel
          </Button>,
          !printOnly && <Button key="png" onClick={exportToPng}>
            Export PNG
          </Button>,
          <Button key="print" type="primary" onClick={trytoprint}>
            {printOnly ? "Print QR" : "Print Barcode"}
          </Button>,
        ]}
      >
        <DatePicker
          onChange={(e) => {
            setmfgdate(e);
          }}
        />
      </Modal>
    </>
  );
};

const ComponentToPrint = React.forwardRef((props, ref) => {
  return (
    <div ref={ref} className={"productBarcodePrint"}>
      <BarcodeLabel currentItem={props.currentItem} />
    </div>
  );
});

const BarcodeLabel = ({ currentItem }) => {
  const barcodeValue = `prod_${currentItem.id}`;
  const sellPrice = Number(currentItem.sell_price || 0).toFixed(2);

  return (
    <div className={"barcodeRotateFrame"}>
      <div className={"barcodeCard"}>
        <div className={"qrWrap"}>
          <QRCode value={barcodeValue} size={64} />
        </div>
        <div className={"textWrap"}>
          <span className={"productName"}>{currentItem.name}</span>
          <span className={"priceText"}>{`PHP ${sellPrice}`}</span>
        </div>
      </div>
    </div>
  );
};

export default Index;
